import React from 'react';
import { motion } from 'framer-motion';
import { HiCode, HiDownload, HiInformationCircle, HiCheck, HiArrowLeft } from 'react-icons/hi';
import { useParams, Link } from 'react-router-dom';

const AppsDetail = () => {
  const { appId } = useParams();

  const handleDownload = (downloadUrl, title) => {
    // Create a temporary anchor element to trigger download
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `${title}-Setup.exe`;
    link.target = '_blank';
    
    // Add to DOM, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Show a notification (you can enhance this with a toast library)
    alert(`Download started for ${title}!`);
  };

  const appData = {
    'delphi-2010': {
      title: 'Delphi 2010',
      description: 'The default IDE used in South African schools according to the CAPS curriculum. Lightweight version for educational purposes.',
      version: '2010',
      size: '~105MB',
      features: [
        'Educational version for CAPS curriculum',
        'Lightweight and optimized for learning',
        'Pascal programming language support',
        'Visual component library',
        'Database connectivity',
        'Report generation tools'
      ],
      requirements: [
        'Windows 7 or later',
        '2GB RAM minimum',
        '500MB free disk space',
        '1024x768 screen resolution'
      ],
      image: '/apps/delphi-2010/delphi-2010.png',
      downloadUrl: 'https://github.com/kryptik-dev/amaandildar.github.io/releases/download/2010/delphi-2010_setup.exe',
      color: 'accent-green'
    },
    'delphi-12': {
      title: 'Delphi 12',
      description: 'The latest version of Delphi for modern development with enhanced features and improved performance.',
      version: '12',
      size: '~1.5GB',
      features: [
        'Modern IDE with enhanced features',
        'Multi-platform development',
        'Advanced debugging tools',
        'Cloud deployment support',
        'Mobile app development',
        'AI-powered code completion'
      ],
      requirements: [
        'Windows 10 or later',
        '8GB RAM recommended',
        '2GB free disk space',
        '1920x1080 screen resolution'
      ],
      image: '/apps/delphi-12/delphi-12.png',
      downloadUrl: 'https://github.com/kryptik-dev/amaandildar.github.io/releases/download/Latest/delphi-12_setup.exe',
      color: 'accent-blue'
    }
  };

  const app = appData[appId];

  if (!app) {
    return (
      <div className="min-h-screen pt-32 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="glass-morphism-strong rounded-2xl p-8">
            <h1 className="text-display-3 font-space-grotesk font-bold gradient-text-green mb-6">
              App Not Found
            </h1>
            <p className="text-gray-300 mb-6">The requested application could not be found.</p>
            <Link to="/" className="btn-primary">
              Return Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-6 pb-20">
      <div className="max-w-6xl mx-auto pt-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Back Button */}
          <div className="mb-6">
            <Link to="/" className="inline-flex items-center text-accent-green hover:text-accent-green/80 transition-colors">
              <HiArrowLeft className="mr-2" />
              Back to Home
            </Link>
          </div>
          
          {/* Header */}
          <div className="md:text-center text-left mb-12">
            <div className="inline-flex items-center space-x-2 glass-morphism px-6 py-3 rounded-full mb-6 md:justify-center justify-start">
              <HiCode className={`text-${app.color}`} />
              <span className={`font-jetbrains text-${app.color} tracking-wider text-sm`}>
                {app.version.toUpperCase()}
              </span>
            </div>
            <h1 className="text-display-2 font-space-grotesk font-bold mb-6 md:text-center text-left">
              <span className="text-white">{app.title} </span>
              <span className={`gradient-text-${app.color.split('-')[1]}`}>IDE</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed md:text-center text-left">
              {app.description}
            </p>
          </div>

                     <div className="grid lg:grid-cols-3 gap-8">
             {/* Main Content */}
             <div className="lg:col-span-2 space-y-8">
               {/* Features */}
              <div className="glass-morphism-strong rounded-2xl p-8">
                <h3 className="text-2xl font-space-grotesk font-bold gradient-text-green mb-6">
                  Key Features
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {app.features.map((feature, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center space-x-3"
                    >
                      <HiCheck className={`text-${app.color} text-lg`} />
                      <span className="text-gray-300">{feature}</span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* System Requirements */}
              <div className="glass-morphism-strong rounded-2xl p-8">
                <h3 className="text-2xl font-space-grotesk font-bold gradient-text-blue mb-6">
                  System Requirements
                </h3>
                <div className="space-y-3">
                  {app.requirements.map((requirement, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center space-x-3"
                    >
                      <HiInformationCircle className="text-accent-blue text-lg" />
                      <span className="text-gray-300">{requirement}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Download Card */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="glass-morphism-strong rounded-2xl p-6"
              >
                                 <div className="md:text-center text-left">
                   <div className={`w-16 h-16 bg-${app.color}/20 rounded-xl flex items-center justify-center mx-auto mb-4`}>
                     <img 
                       src={app.image} 
                       alt={app.title}
                       className="w-10 h-10 object-contain"
                     />
                   </div>
                  <h3 className="text-xl font-space-grotesk font-bold text-white mb-2 md:text-center text-left">
                    Download {app.title}
                  </h3>
                  <p className="text-gray-300 mb-4 md:text-center text-left">
                    File size: {app.size}
                  </p>
                                     <button 
                     className="btn-primary w-full"
                     onClick={() => handleDownload(app.downloadUrl, app.title)}
                   >
                     Download Now →
                   </button>
                </div>
              </motion.div>

              {/* Quick Info */}
              <div className="glass-morphism-strong rounded-2xl p-6">
                <h4 className="text-lg font-space-grotesk font-bold text-white mb-4">
                  Quick Info
                </h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Version:</span>
                    <span className="text-white">{app.version}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Size:</span>
                    <span className="text-white">{app.size}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Platform:</span>
                    <span className="text-white">Windows</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">License:</span>
                    <span className="text-white">Educational</span>
                  </div>
                </div>
              </div>

              {/* Back to Apps */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="glass-morphism-strong rounded-2xl p-6"
              >
                <Link to="/#apps" className="block md:text-center text-left">
                  <span className="text-accent-green hover:text-accent-green/80 transition-colors">
                    ← Back to Applications
                  </span>
                </Link>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AppsDetail; 